import styled from 'styled-components';
import { Colors } from '../constants/colors';
import React, { ReactNode, useEffect, useState } from 'react';
import { linearGradient, rgba } from 'polished';
import { Fonts } from '../constants/fonts';
import { Device } from '../constants/device';

type BoxProps = {
  title?: string,
  order?: number,
  description?: string,
  summary?: string,
  disabled?: boolean,
  children?: ReactNode
}

export const SectionBox = (props: BoxProps) => {
  const [collapsed, setCollapsed] = useState(props.disabled === true);

  useEffect(() => {
    setCollapsed(props.disabled === true);
  }, [props.disabled]);

  return (
    <StyledBox disabled={props.disabled}>
      <BoxTitle>
        {props.order && <BoxOrder>{props.order}</BoxOrder>}
        <h1>{props.title}</h1>
        {props.description?.length && !collapsed && <BoxDescription>{props.description}</BoxDescription>}
      </BoxTitle>
      <BoxContent onClick={() => collapsed ? setCollapsed(false) : {}}>
        {(() => {
          if (props.disabled) {
            return null;
          } else if (collapsed) {
            return props.summary;
          } else {
            return props.children;
          }
        })()}
      </BoxContent>
    </StyledBox>
  );
};

const StyledBox = styled.div<BoxProps>`
  background: ${Colors.white};
  border-radius: 6px;
  box-shadow: rgba(0, 0, 0, 0.1) 0 4px 12px;
  margin: 25px 0;
  overflow: hidden;

  display: flex;

  ${Device.downLaptop} {
    flex-direction: column;
  }

  ${({ disabled }) => disabled && `
    pointer-events: none;
    opacity: 0.5;

    ${BoxTitle} {
      background: ${Colors.grey};
    }

    ${BoxContent} {
      color: ${Colors.grey};
    }
  `}
`;

const BoxOrder = styled.span`
  position: absolute;
  z-index: 1;
  font-size: 120px;
  color: ${rgba(Colors.white, 0.2)};
  font-family: ${Fonts.secondary};
  font-weight: 900;
  line-height: 1;
  top: 0;
  left: 10px;
`;

const BoxTitle = styled.div`
  background: ${linearGradient({
    colorStops: [Colors.primaryDarker, Colors.primary],
    toDirection: 'to bottom right',
    fallback: Colors.primary
  })};
  color: ${Colors.white};
  padding: 30px 30px 30px 50px;
  width: 430px;
  flex-basis: 430px;
  position: relative;

  > *:not(${BoxOrder}) {
    position: relative;
    z-index: 2;
  }

  ${Device.downDesktop} {
    width: 30%;
    flex-basis: 30%;
  }

  ${Device.downLaptop} {
    width: 100%;
    flex-basis: 100%;

    h1 {
      margin: 0 0 20px;
    }
  }

  ${Device.downTablet} {
    font-size: 14px;

    h1 {
      font-size: 22px;
      margin: 0 0 10px;
    }
  }
`;

const BoxDescription = styled.p`
  margin: 40px 0 30px;
  color: ${Colors.white};
  opacity: 0.8;

  ${Device.downLaptop} {
    margin: 0;
  }
`;

const BoxContent = styled.div`
  background: ${Colors.white};
  padding: 50px 50px 50px 30px;
  flex: 1;

  display: flex;
  flex-direction: column;
  justify-content: center;

  width: calc(100% - 430px);
  flex-basis: calc(100% - 430px);

  ${Device.downDesktop} {
    width: 70%;
    flex-basis: 70%;
  }

  ${Device.downLaptop} {
    padding: 30px;
    width: 100%;
    flex-basis: 100%;
  }
`;
